import { i18n } from '@lingui/core'

export type SupportedLocale = 'fr' | 'en'

export const defaultLocale: SupportedLocale = 'fr'
export const supportedLocales: SupportedLocale[] = ['fr', 'en']

// Log missing translations in development
i18n.on('missing', ({ id, locale }) => {
  if (import.meta.env.DEV) {
    console.warn(`Missing translation for "${id}" in locale "${locale}"`)
  }
})

// Cache for loaded catalogs to avoid re-loading
const catalogCache = new Map<SupportedLocale, Record<string, string>>()

/**
 * Load messages for a given locale with caching and lazy loading
 */
export async function loadCatalog(locale: SupportedLocale) {
  // Return cached catalog if available
  if (catalogCache.has(locale)) {
    return catalogCache.get(locale)
  }

  // Dynamic import for lazy loading - this creates separate chunks
  let messages: Record<string, string>
  
  if (locale === 'fr') {
    const module = await import(/* webpackChunkName: "locale-fr" */ '../locales/fr/messages.mjs') as { messages: Record<string, string> }
    messages = module.messages
  } else if (locale === 'en') {
    const module = await import(/* webpackChunkName: "locale-en" */ '../locales/en/messages.mjs') as { messages: Record<string, string> }
    messages = module.messages
  } else {
    throw new Error(`Unsupported locale: ${locale}`)
  }
  
  // Cache the loaded catalog
  catalogCache.set(locale, messages)
  
  return messages
}

/**
 * Initialize i18n with a specific locale
 * Always loads French as fallback for missing translations
 */
export async function initializeI18n(locale: SupportedLocale = defaultLocale) {
  // Always load French (source locale) as fallback
  const frMessages = await loadCatalog('fr')
  const messages = locale === 'fr' ? frMessages : await loadCatalog(locale)
  
  i18n.load({
    fr: frMessages,
    [locale]: messages,
  })
  
  i18n.activate(locale)
  
  return i18n
}

/**
 * Dynamically load and activate a locale
 * Always loads French as fallback for missing translations
 */
export async function activateLocale(locale: SupportedLocale) {
  // Always load French (source locale) as fallback
  const frMessages = await loadCatalog('fr')
  const messages = locale === 'fr' ? frMessages : await loadCatalog(locale)
  
  // Load both locales so fallback works
  i18n.load({
    fr: frMessages,
    [locale]: messages,
  })
  
  i18n.activate(locale)
}

// Initialize with default locale messages immediately
async function initializeDefaultLocale() {
  try {
    const messages = await loadCatalog(defaultLocale)
    i18n.load({
      [defaultLocale]: messages,
    })
    i18n.activate(defaultLocale)
  } catch (error) {
    console.error('Failed to initialize default locale:', error)
    // Fallback to empty activation
    i18n.activate(defaultLocale)
  }
}

// Initialize immediately
initializeDefaultLocale()

export { i18n }